import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, CheckCircle, FileText, Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../api/authService';
import jobPostingApi from '../../api/jobPosting';
import subscriptionApi from '../../api/subscription';
import Button from '../../components/common/Button';
import JobManagementCard from '../../components/recruiter/JobManagementCard';
import ScoringOverviewModal from '../../components/recruiter/ScoringOverviewModal';

const JOBS_PER_PAGE = 5;
const STATUS_TABS = ['all', 'active', 'draft', 'closed'];
const SEARCH_DEBOUNCE_MS = 400;

const ManageJobListingsPage = () => {
    const navigate = useNavigate();
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [jobToDelete, setJobToDelete] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [scoringModalOpen, setScoringModalOpen] = useState(false);
    const [scoringData, setScoringData] = useState(null);
    const [scoringLoading, setScoringLoading] = useState(false);

    const debounceTimer = useRef(null);

    const showNotification = useCallback((message, type) => {
        setNotification({ message, type });
        setTimeout(() => setNotification({ message: '', type: '' }), 4000);
    }, []);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setSearchTerm(value);
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);
    };

    useEffect(() => () => clearTimeout(debounceTimer.current), []);

    const { data: subscriptionData } = useQuery({
        queryKey: ['subscription-data'],
        queryFn: async () => {
            const res = await subscriptionApi.getMySubscription();
            return res.data;
        },
    });

    const { data: stats } = useQuery({
        queryKey: ['application-stats'],
        queryFn: async () => {
            const res = await authService.getApplicationStats();
            return res.data;
        },
    });

    const {
        data: jobsPage,
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: ['my-job-postings', currentPage, searchTerm, statusFilter],
        queryFn: async () => {
            const params = { page: currentPage };
            if (searchTerm) params.search = searchTerm;
            if (statusFilter !== 'all') params.status = statusFilter;
            const res = await jobPostingApi.getMyJobPostings(params);
            return res.data; // { results, count, next, previous }
        },
        keepPreviousData: true,
        placeholderData: (prev) => prev,
    });

    // Merge applicant counts from stats into the current page's jobs
    const jobs = useMemo(() => {
        if (!jobsPage?.results) return [];
        return jobsPage.results.map((job) => ({
            ...job,
            applicants_count: stats?.job_breakdown?.[job.id]?.count ?? 0,
        }));
    }, [jobsPage, stats]);

    const totalCount = jobsPage?.count ?? 0;
    const totalPages = Math.ceil(totalCount / JOBS_PER_PAGE);

    const handleStatusToggle = async (jobId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'draft' : 'active';
        try {
            await jobPostingApi.updateJobPosting(jobId, { status: newStatus });
            refetch();
            showNotification(`Job has been set to ${newStatus}.`, 'success');
        } catch {
            showNotification('Failed to update job status.', 'error');
        }
    };

    const confirmDelete = (job) => {
        setJobToDelete(job);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!jobToDelete) return;
        try {
            await jobPostingApi.deleteJobPosting(jobToDelete.id);

            const remainingOnPage = jobs.length - 1;
            if (remainingOnPage === 0 && currentPage > 1) {
                setCurrentPage((p) => p - 1);
            } else {
                refetch();
            }
            showNotification(`Job "${jobToDelete.title}" has been deleted.`, 'success');
        } catch {
            showNotification('Failed to delete job posting.', 'error');
        } finally {
            setShowDeleteModal(false);
            setJobToDelete(null);
        }
    };

    const fetchScoringOverview = useCallback(async (jobId) => {
        setScoringLoading(true);
        try {
            const response = await authService.getScoringOverview(jobId);
            setScoringData(response.data);
        } catch {
            showNotification('Failed to load scoring overview', 'error');
        } finally {
            setScoringLoading(false);
        }
    }, [showNotification]);

    const handleViewScoring = (jobId) => {
        setScoringModalOpen(true);
        fetchScoringOverview(jobId);
    };

    const handleCloseScoringModal = () => {
        setScoringModalOpen(false);
        setScoringData(null);
    };

    const handlePostNewJob = () => {
        navigate(subscriptionData?.has_subscription ? '/post-job' : '/pricing');
    };

    if (isLoading && !jobsPage) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-160px)]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="max-w-7xl mx-auto py-8 px-4 text-center">
                <p className="text-red-600">Failed to fetch job listings. Please try again.</p>
                <Button onClick={refetch} className="mt-4">Try Again</Button>
            </div>
        );
    }

    return (
        <div className="bg-secondary-50 min-h-screen">
            <div className="max-w-7xl mx-auto py-8 px-4">

                {notification.message && (
                    <div className={`fixed top-24 right-4 z-50 p-4 rounded-lg shadow-lg border flex items-center ${
                        notification.type === 'success'
                            ? 'bg-green-50 border-green-200 text-green-800'
                            : 'bg-red-50 border-red-200 text-red-800'
                    }`}>
                        <CheckCircle className="w-5 h-5 mr-3" />
                        {notification.message}
                    </div>
                )}

                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-secondary-900">Manage Jobs</h1>
                        <p className="mt-1 text-secondary-600">Edit, pause, or delete your job postings.</p>
                    </div>
                    <Button onClick={handlePostNewJob} icon={<Plus className="w-4 h-4 mr-2" />}>
                        Post New Job
                    </Button>
                </div>

                {/* Search & filter bar */}
                <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-100 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        <div className="md:col-span-8 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search by title or location..."
                                value={searchInput}
                                onChange={handleSearchChange}
                                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                            />
                        </div>
                        <div className="md:col-span-4">
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="w-full p-2.5 border rounded-lg bg-white"
                            >
                                {STATUS_TABS.map((tab) => (
                                    <option key={tab} value={tab}>
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className={`space-y-4 transition-opacity duration-150 ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}>
                    {jobs.length === 0 ? (
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 text-center">
                            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                            <h3 className="text-xl font-semibold mb-2">No Matching Jobs Found</h3>
                            <p className="text-gray-600">Try adjusting your search or filters.</p>
                        </div>
                    ) : (
                        jobs.map((job) => (
                            <JobManagementCard
                                key={job.id}
                                job={job}
                                onNavigate={navigate}
                                onStatusToggle={handleStatusToggle}
                                onDelete={confirmDelete}
                                onViewScoring={handleViewScoring}
                            />
                        ))
                    )}
                </div>

                {totalPages > 1 && (
                    <div className="mt-6 flex justify-between items-center">
                        <Button
                            onClick={() => setCurrentPage((p) => p - 1)}
                            disabled={currentPage === 1}
                            variant="secondary"
                            icon={<ArrowLeft size={16} />}
                        >
                            Previous
                        </Button>
                        <span className="text-sm text-gray-600">
                            Page {currentPage} of {totalPages}
                            <span className="ml-2 text-gray-400">({totalCount} total)</span>
                        </span>
                        <Button
                            onClick={() => setCurrentPage((p) => p + 1)}
                            disabled={currentPage === totalPages}
                            variant="secondary"
                            icon={<ArrowRight size={16} />}
                            iconPosition="right"
                        >
                            Next
                        </Button>
                    </div>
                )}

                {showDeleteModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full">
                            <h3 className="text-lg font-bold">Confirm Deletion</h3>
                            <p className="text-sm text-gray-600 mt-2">
                                Are you sure you want to delete the job posting for "{jobToDelete?.title}"?
                                This action cannot be undone.
                            </p>
                            <div className="mt-6 flex justify-end space-x-2">
                                <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                                    Cancel
                                </Button>
                                <Button className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>
                                    Delete
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Scoring overview modal */}
                <ScoringOverviewModal
                    open={scoringModalOpen}
                    onClose={handleCloseScoringModal}
                    scoringData={scoringData}
                    isLoading={scoringLoading}
                    title="Applicant Scoring Overview"
                />
            </div>
        </div>
    );
};

export default ManageJobListingsPage;